
import Store from '@/store';
import { minutesToHumanDuration } from '@/utils/dateHelpers';
import { IProduct as ProductModel } from '@homeserve/od-typings/api';
import { IVoucher, productHelper, voucherHelper } from '@homeserve/vue-api-plugin';
import { DEFAULT_VAT_RATE, domainsToSkipFixAppointment } from '@homeserve/vue-components-plugin/src/constants';
import {
  breadcrumbHelper,
  getProductPriceDetail,
  IPostCode,
  isVoucherValid,
  PriceBreakDown,
  PriceOption,
  TimeSlot,
} from '@homeserve/vue-components-plugin/src/main';
import GTMPageViewingMixin from '@homeserve/vue-components-plugin/src/mixins/GTMPageViewingMixin';
import { getCurrentSlot } from '@homeserve/vue-components-plugin/src/utils/dateHelpers';
import { Affix } from 'vue-affix';
import { Component, Mixins } from 'vue-property-decorator';
import { State } from 'vuex-class';
import ProductEstimateAffixMixin from '../mixins/ProductEstimateAffixMixin';

@Component({
  components: {
    Affix,
    WLLinkedProducts: () => import('@homeserve/vue-components-plugin/src/components/WLProduct/WLLinkedProducts.vue'),
    WLProductPrestationInfos: () =>
      import('@homeserve/vue-components-plugin/src/components/WLProduct/WLProductPrestationInfos.vue'),
    WLProductContext: () => import('@homeserve/vue-components-plugin/src/components/WLProduct/WLProductContext.vue'),
    WLProductMoreDetails: () =>
      import('@homeserve/vue-components-plugin/src/components/WLProduct/WLProductMoreDetails.vue'),
    WLHomeServeExpert: () => import('@homeserve/vue-components-plugin/src/components/WLHomepage/WLHomeServeExpert.vue'),
    WLProductQuote: () => import('@homeserve/vue-components-plugin/src/components/WLProduct/WLProductQuote.vue'),
  },
})
export default class Product extends Mixins(GTMPageViewingMixin, ProductEstimateAffixMixin) {
  @State('voucher', { namespace: 'api/voucher/voucher' })
  private voucher!: IVoucher;

  private currentSlot = getCurrentSlot();

  get isPriceSlotDependant() {
    return domainsToSkipFixAppointment.indexOf(this.product.domain.slug) !== -1;
  }

  get voucherValid() {
    const slot: TimeSlot = this.isPriceSlotDependant
      ? this.currentSlot
      : { cost: PriceOption.customerBasePurchasePrice };

    const cost = slot.cost || PriceOption.customerBasePurchasePrice;

    return this.voucher && isVoucherValid(this.voucher, this.product[cost]);
  }

  get priceDetails() {
    const slot: TimeSlot = this.isPriceSlotDependant
      ? this.currentSlot
      : { cost: PriceOption.customerBasePurchasePrice };

    return getProductPriceDetail({
      product: this.product,
      slot,
      housingOlderThan2Years: true,
      voucher: this.voucherValid ? this.voucher : undefined,
    });
  }

  get price() {
    return this.priceDetails.priceIncVat;
  }

  get discount() {
    return this.priceDetails.discount;
  }

  get productEstimatePriceBreakdown() {
    const breakDown: PriceBreakDown[] = [];
    if (this.product.customerSparePartsCost) {
      breakDown.push({
        name: this.i18n.spare_parts_included,
        price: this.product.customerSparePartsCost,
      });
    }
    if (this.product.customerCallOutPrice) {
      breakDown.push({
        name: this.i18n.callout_included,
        price: this.product.customerCallOutPrice,
      });
    }
    if (this.product.customerJobWorkforce) {
      breakDown.push({
        name: this.i18n.workforce_included,
        price: this.product.customerJobWorkforce,
      });
    }
    if (
      (this.currentSlot.cost === PriceOption.customerExtraChargeNights ||
        this.currentSlot.cost === PriceOption.customerExtraChargeSundays) &&
      this.isPriceSlotDependant
    ) {
      breakDown.push({
        name: this.i18n.additionnal_fees_nights_and_sundays,
        price: this.product[this.currentSlot.cost],
      });
    }
    if (this.voucherValid) {
      const name = `${this.i18n.discount_code} ${this.voucher.type === 'percent' ? `(-${this.voucher.amount}%)` : ''}`;
      breakDown.push({
        name,
        price: this.discount * -1,
        button: {
          icon: 'close',
          action: () => voucherHelper.setVoucher(this.$store, null),
        },
      });
    }

    return breakDown;
  }

  get defaultVatRate() {
    return DEFAULT_VAT_RATE;
  }
  private i18n: any;

  private postCode: IPostCode = { postCode: '', summary: '', town: '' };

  @State('postCode', { namespace: 'app/diagnostic' })
  private postCodeStore!: IPostCode;

  @State('product', { namespace: 'api/catalog/product' })
  private product!: ProductModel;

  @State('errorFetchProductBySlug', { namespace: 'api/catalog/product' })
  private errorFetchProductBySlug!: boolean;

  private slug!: string;

  get gtmRouterInfo() {
    return {
      domain: this.product ? (this.product.domain ? this.product.domain.name : '') : '',
      ecommerce: {
        detail: {
          products: [
            {
              name: this.product.name,
              id: this.product.id,
              price: this.product.customerBasePurchasePrice,
              category: `${this.product.domain.name}|${this.product.category.name}`,
              dimension10: this.postCode,
            },
          ],
        },
      },
    };
  }

  public async beforeRouteEnter(to, from, next) {
    await initContainer(to, from, next);
  }

  public async beforeRouteUpdate(to, from, next) {
    await initContainer(to, from, next);
  }

  public async created() {
    this.postCode = this.postCodeStore;
    this.i18n = {
      advices: this.$t('product_page.advices', {
        domain: this.product.domain.name,
      }),
      callout_included: this.$t('product_page.callout_included'),
      order_my_repair: this.$t('product_page.order_my_repair'),
      esca_certification: this.$t('footer.esca_certification'),
      excluded_in_job: this.$t('product_page.excluded_in_job'),
      excluded_in_job_details: this.$t('product_page.excluded_in_job_details'),
      included_in_job: this.$t('product_page.included_in_job'),
      spare_parts_included: this.$t('product_page.spare_parts_included'),
      additionnal_fees: this.$t('product_page.additionnal_fees'),
      additionnal_fees_nights_and_sundays: this.$t('product_page.additionnal_fees_nights_and_sundays'),
      workforce_included: this.$t('product_page.workforce_included', {
        duration: this.product
          ? this.product.customerJobDuration
            ? minutesToHumanDuration(this.product.customerJobDuration)
            : ''
          : '',
      }),
      discount_code: this.$t('product_page.discount_code'),
      error_invalid_postcode: this.$t('diagnosis.error_invalid_postcode'),
      error_not_covered_postcode: this.$t('diagnosis.error_not_covered_postcode'),
      jobMoreDetails: this.$t('product_page.moreDetails'),
      title: this.$t('product_page.title'),
      title_order: this.$t('product_page.title_order', {
        domain: this.product.domain.name,
      }),
    };
    this.pushBreadcrumb();
  }

  private pushBreadcrumb() {
    breadcrumbHelper.setDomain(this.$store, this.product.domain.slug);
    breadcrumbHelper.push(this.$store, {
      name: this.product.name,
      link: '',
    });
  }
  private async updatePostCode(postCode) {
    this.postCode = postCode;
  }
}

async function initContainer(to, from, next) {
  await productHelper.fetchProductBySlug(Store, to.params.slug);

  if (Store.getters['api/catalog/product/errorFetchProductBySlug']) {
    const domainSlug = await productHelper.fetchDomainSlugByProductSlug(Store, to.params.slug);

    if (domainSlug) return next('/autodiagnostic/' + domainSlug.result);
    else return next('/404');
  }

  document.title = Store.getters['api/catalog/product/product']
    ? Store.getters['api/catalog/product/product'].metaTitle
    : '';

  next();
}
